import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Activity, History, FolderOpen, BarChart3, Settings,
  Shield, LogOut, Menu, X as CloseIcon, Eye, Sun, Moon,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useDemo } from '../context/DemoContext';
import { useTheme } from '../context/ThemeContext';
import { APP, NAV } from '../design-system/content';
import { DemoBadge } from './DemoBadge';
import { useCursorParallax } from '../hooks/useCursorParallax';

/**
 * AppLayout — the shell for every page that isn't the Workspace (Analyze)
 * workspace flagship. Delivers the liquid-glass sidebar, cursor-parallax
 * aurora shell, and mobile nav consistently across all pages.
 */
export function AppLayout({ children, activeNav }) {
  const { user, logout } = useAuth();
  const { demoMode } = useDemo();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Ambient parallax on the aurora bg blobs (reduced-motion safe)
  useCursorParallax({ strength: 14 });

  const handleLogout = async () => { await logout(); navigate('/'); };
  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  const isAdmin = user?.role === 'admin';
  const isReviewer = user?.role === 'reviewer';

  return (
    <div className="liquid-shell min-h-screen lg:h-screen lg:overflow-hidden flex flex-col" data-testid="app-shell">
      {/* Skip to content (accessibility) */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:bg-[hsl(var(--accent-cyan))] focus:text-white focus:rounded-md">
        Skip to content
      </a>

      {/* Mobile top header */}
      <header className="lg:hidden msml-glass sticky top-0 z-40" role="banner">
        <div className="flex items-center justify-between px-4 h-14">
          <Link to="/workspace" className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[linear-gradient(135deg,hsl(var(--accent-cyan)),hsl(var(--accent-teal)))] shadow-[0_0_12px_hsl(var(--accent-cyan)/0.4)]">
              <Activity className="w-4 h-4 text-white" />
            </span>
            <span className="font-semibold text-ink tracking-tight">{APP.name}</span>
            <DemoBadge />
          </Link>
          <div className="flex items-center gap-1">
            <button onClick={toggleTheme} className="p-2 text-ink-2 hover:text-ink" data-testid="mobile-theme-toggle" aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-ink-2 hover:text-ink" data-testid="mobile-menu-btn" aria-label="Toggle navigation menu" aria-expanded={mobileMenuOpen}>
              {mobileMenuOpen ? <CloseIcon className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <nav className="border-t border-[var(--glass-stroke)] bg-[var(--glass-fill-panel)] backdrop-blur-xl p-3 space-y-1">
            <NavItem to="/workspace" icon={<Activity />} label={NAV.workspace} active={activeNav === 'analyze'} />
            <NavItem to="/history"   icon={<History />}    label={NAV.history}   active={activeNav === 'history'} />
            <NavItem to="/cases"     icon={<FolderOpen />} label={NAV.cases}     active={activeNav === 'cases'} />
            <NavItem to="/reports"   icon={<BarChart3 />}  label={NAV.reports}   active={activeNav === 'reports'} />
            {demoMode && <NavItem to="/demo" icon={<Eye />} label="Demo" active={activeNav === 'demo'} />}
            <NavItem to="/settings"  icon={<Settings />}   label={NAV.settings}  active={activeNav === 'settings'} />
            {isAdmin && <NavItem to="/admin" icon={<Shield />} label={NAV.admin} active={activeNav === 'admin'} />}
            {isReviewer && (
              <span className="block px-3 py-1 text-[10px] text-ink-muted uppercase tracking-wider">Read-only reviewer</span>
            )}
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-ink-2 hover:text-ink rounded-md transition-colors">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </nav>
        )}
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Desktop liquid-glass sidebar */}
        <aside className="hidden lg:flex flex-col w-[248px] liquid-sidebar shrink-0" role="navigation" aria-label="Main navigation" data-testid="app-sidebar">
          <Link to="/workspace" className="flex items-center gap-2.5 px-5 h-16 border-b border-[var(--glass-stroke)]">
            <span className="w-8 h-8 rounded-xl flex items-center justify-center bg-[linear-gradient(135deg,hsl(var(--accent-cyan)),hsl(var(--accent-teal)))] shadow-[inset_0_1px_0_rgba(255,255,255,0.2),0_0_16px_hsl(var(--accent-cyan)/0.35)]" aria-hidden>
              <Activity className="w-4 h-4 text-white" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-ink tracking-tight text-[0.95rem]">{APP.name}</span>
                <DemoBadge />
              </div>
              <span className="block text-[10px] font-mono uppercase tracking-[0.22em] text-ink-muted leading-none mt-0.5">HQ · Evaluation</span>
            </div>
          </Link>
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto scrollbar-thin">
            <NavItem to="/workspace" icon={<Activity />}    label={NAV.workspace} active={activeNav === 'analyze'} />
            <NavItem to="/history"   icon={<History />}     label={NAV.history}   active={activeNav === 'history'} />
            <NavItem to="/cases"     icon={<FolderOpen />}  label={NAV.cases}     active={activeNav === 'cases'} />
            <NavItem to="/reports"   icon={<BarChart3 />}   label={NAV.reports}   active={activeNav === 'reports'} />
            {demoMode && <NavItem to="/demo" icon={<Eye />} label="Demo" active={activeNav === 'demo'} />}
            <NavItem to="/settings"  icon={<Settings />}    label={NAV.settings}  active={activeNav === 'settings'} />
            {isAdmin && <NavItem to="/admin" icon={<Shield />} label={NAV.admin} active={activeNav === 'admin'} />}
          </nav>
          <div className="p-3 border-t border-[var(--glass-stroke)] space-y-2">
            <button
              onClick={toggleTheme}
              className="liquid-nav-item w-full justify-between"
              data-testid="theme-toggle-btn"
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              <span className="inline-flex items-center gap-3">
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                {theme === 'dark' ? 'Light mode' : 'Dark mode'}
              </span>
            </button>
            {isReviewer && (
              <div className="px-3">
                <span className="text-[10px] text-ink-muted uppercase tracking-wider">Reviewer access</span>
              </div>
            )}
            <div className="liquid-card p-2.5 flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full flex items-center justify-center bg-[linear-gradient(135deg,hsl(var(--accent-cyan)/0.3),hsl(var(--accent-violet)/0.3))] text-ink font-medium text-sm border border-[var(--glass-stroke-strong)]">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-ink truncate">{user?.name}</p>
                <p className="text-[10px] text-ink-muted truncate">{user?.email}</p>
              </div>
              <button onClick={handleLogout} className="p-1.5 rounded-md text-ink-3 hover:text-ink transition-colors" data-testid="sidebar-logout-btn" title="Sign out" aria-label="Sign out">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </aside>

        <main id="main-content" className="flex-1 overflow-y-auto pb-16 lg:pb-0" role="main">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 msml-glass border-t border-[var(--glass-stroke)] z-40" aria-label="Mobile navigation">
        <div className="flex items-center justify-around py-2">
          <MobileNavItem to="/workspace" icon={<Activity />}   label={NAV.workspace} active={activeNav === 'analyze'} />
          <MobileNavItem to="/history"   icon={<History />}    label={NAV.history}   active={activeNav === 'history'} />
          {demoMode
            ? <MobileNavItem to="/demo"  icon={<Eye />}        label="Demo"           active={activeNav === 'demo'} />
            : <MobileNavItem to="/cases" icon={<FolderOpen />} label={NAV.cases}      active={activeNav === 'cases'} />}
          <MobileNavItem to="/reports"   icon={<BarChart3 />}  label={NAV.reports}   active={activeNav === 'reports'} />
          <MobileNavItem to="/settings"  icon={<Settings />}   label={NAV.settings}  active={activeNav === 'settings'} />
        </div>
      </nav>
    </div>
  );
}

function NavItem({ to, icon, label, active }) {
  return (
    <Link
      to={to}
      className="liquid-nav-item"
      data-active={active ? 'true' : 'false'}
      data-testid={`nav-${label.toLowerCase()}`}
    >
      {React.cloneElement(icon, { className: 'w-4 h-4 shrink-0' })}
      <span className="truncate">{label}</span>
    </Link>
  );
}

function MobileNavItem({ to, icon, label, active }) {
  return (
    <Link
      to={to}
      className={`flex flex-col items-center gap-1 px-3 py-1 text-[11px] font-medium transition-colors ${active ? 'text-[hsl(var(--accent-cyan))]' : 'text-ink-3'}`}
      data-testid={`mobile-nav-${label.toLowerCase()}`}
    >
      {React.cloneElement(icon, { className: 'w-5 h-5' })}
      {label}
    </Link>
  );
}
